import type { RefObject } from 'react';
import { useLayoutEffect, useRef } from 'react';
import type { Updater } from 'use-immer';
import type { SurfaceState } from './useSurface';

interface SelectionStrategy {
  surfacePointerDown?: (update: Updater<SurfaceState>) => void;
  isEditing(editingLayerIndex: number | undefined, layerIndex: number): boolean;
  /**
   * `skipCaretInit` is set by "Edit" mode, where every inline-editable text layer is editing from
   * the moment it mounts. Placing the caret would then fire for every field at once, and since
   * the document has a single selection they would fight — the last one to run would steal the
   * caret from whichever field the pointer actually landed in. Let the browser place it instead.
   */
  useInitTextCaret(isEditing: boolean, skipCaretInit?: boolean): RefObject<HTMLDivElement>;
}

const ClickSelectionStrategy: SelectionStrategy = {
  surfacePointerDown: update => {
    update(draft => {
      draft.activeLayerIndex = undefined;
    });
  },
  isEditing(editingLayerIndex: number | undefined, layerIndex: number) {
    return editingLayerIndex === layerIndex;
  },
  useInitTextCaret(isEditing: boolean, skipCaretInit?: boolean) {
    const contentEditableRef = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
      if (skipCaretInit) return;
      if (!isEditing) return;
      if (!contentEditableRef.current) return;
      const range = document.createRange();
      range.selectNodeContents(contentEditableRef.current);
      range.collapse(false);
      const sel = window.getSelection();
      sel?.removeAllRanges();
      sel?.addRange(range);
    }, [isEditing, skipCaretInit]);

    return contentEditableRef;
  },
};

export const CurrentSelectionStrategy = ClickSelectionStrategy;
